import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Check, X, ChevronDown, Zap, Shield, Globe, Smartphone, BarChart2, MessageCircle, Users, Star } from 'lucide-react';
import { FloatingPaths } from '@LandingFunctionalDisplay/BackgroundPaths';
import Footer from '@CommonFunctionalDisplay/Footer';
import TopPanel from '@CommonFunctionalDisplay/TopPanel';
import useSEO from '@Hooks/useSEO';
import './PricingPage.scss';

// ─── Data ─────────────────────────────────────────────────────────────────────

const PLANS = [
    {
        id: 'free',
        name: 'Free',
        tagline: 'Get started at no cost',
        monthlyPrice: 0,
        annualPrice: 0,
        priceNote: 'Forever free',
        agents: '1 agent',
        forms: '1 form',
        tickets: '10 tickets / month',
        overage: null,
        cta: 'Get Started Free',
        ctaVariant: 'outline',
        popular: false,
    },
    {
        id: 'starter',
        name: 'Starter',
        tagline: 'Great for small support teams',
        monthlyPrice: 5990,
        annualPrice: 4792,
        priceNote: '/ month',
        agents: 'Unlimited agents',
        forms: '3 forms',
        tickets: '500 tickets / month',
        overage: '₹10 / extra ticket',
        cta: 'Start Free Trial',
        ctaVariant: 'outline',
        popular: false,
    },
    {
        id: 'growth',
        name: 'Growth',
        tagline: 'Scale your support operations',
        monthlyPrice: 9990,
        annualPrice: 7992,
        priceNote: '/ month',
        agents: 'Unlimited agents',
        forms: 'Unlimited forms',
        tickets: '2,000 tickets / month',
        overage: '₹7.50 / extra ticket',
        cta: 'Start Free Trial',
        ctaVariant: 'solid',
        popular: true,
    },
    {
        id: 'enterprise',
        name: 'Enterprise',
        tagline: 'Built for large organisations',
        monthlyPrice: null,
        annualPrice: null,
        priceNote: 'Custom pricing',
        agents: 'Unlimited agents',
        forms: 'Unlimited forms',
        tickets: 'Unlimited tickets',
        overage: null,
        cta: 'Contact Sales',
        ctaVariant: 'dark',
        popular: false,
    },
];

const FEATURE_CATEGORIES = [
    {
        name: 'Forms & Submission',
        icon: <Globe size={15} />,
        rows: [
            { label: 'Custom forms',           free: true,       starter: true,            growth: true,           enterprise: true },
            { label: 'Form fields',            free: 'Up to 5',  starter: 'Up to 15',      growth: 'Unlimited',    enterprise: 'Unlimited' },
            { label: 'File attachments',       free: false,      starter: true,            growth: true,           enterprise: true },
            { label: 'Public form URL',        free: true,       starter: true,            growth: true,           enterprise: true },
            { label: 'Custom form branding',   free: false,      starter: false,           growth: true,           enterprise: true },
        ],
    },
    {
        name: 'Tickets & Management',
        icon: <Zap size={15} />,
        rows: [
            { label: 'Ticket creation',        free: true,       starter: true,            growth: true,           enterprise: true },
            { label: 'Ticket comments',        free: true,       starter: true,            growth: true,           enterprise: true },
            { label: 'Ticket history',         free: '7 days',   starter: '30 days',       growth: '1 year',       enterprise: 'Unlimited' },
            { label: 'Bulk actions',           free: false,      starter: true,            growth: true,           enterprise: true },
            { label: 'Custom ticket statuses', free: false,      starter: false,           growth: true,           enterprise: true },
        ],
    },
    {
        name: 'Assignment & Escalation',
        icon: <Users size={15} />,
        rows: [
            { label: 'Manual assignment',      free: true,       starter: true,            growth: true,           enterprise: true },
            { label: 'Auto-assignment rules',  free: false,      starter: true,            growth: true,           enterprise: true },
            { label: 'Escalation matrix',      free: false,      starter: false,           growth: true,           enterprise: true },
            { label: 'Round-robin routing',    free: false,      starter: false,           growth: true,           enterprise: true },
        ],
    },
    {
        name: 'SLA Management',
        icon: <Shield size={15} />,
        rows: [
            { label: 'SLA policies',           free: false,      starter: '1 policy',      growth: 'Unlimited',    enterprise: 'Unlimited' },
            { label: 'SLA breach alerts',      free: false,      starter: true,            growth: true,           enterprise: true },
            { label: 'Priority-based SLA',     free: false,      starter: false,           growth: true,           enterprise: true },
            { label: 'SLA reporting',          free: false,      starter: false,           growth: true,           enterprise: true },
        ],
    },
    {
        name: 'WhatsApp Integration',
        icon: <MessageCircle size={15} />,
        rows: [
            { label: 'WhatsApp notifications', free: false,      starter: true,            growth: true,           enterprise: true },
            { label: 'Ticket updates via WA',  free: false,      starter: true,            growth: true,           enterprise: true },
            { label: 'WhatsApp replies',       free: false,      starter: false,           growth: true,           enterprise: true },
            { label: 'Custom WA templates',    free: false,      starter: false,           growth: true,           enterprise: true },
        ],
    },
    {
        name: 'Analytics & Labels',
        icon: <BarChart2 size={15} />,
        rows: [
            { label: 'Basic dashboard',        free: true,       starter: true,            growth: true,           enterprise: true },
            { label: 'Custom labels/tags',     free: false,      starter: true,            growth: true,           enterprise: true },
            { label: 'Advanced reports',       free: false,      starter: false,           growth: true,           enterprise: true },
            { label: 'Export to CSV/PDF',      free: false,      starter: true,            growth: true,           enterprise: true },
            { label: 'Custom dashboards',      free: false,      starter: false,           growth: false,          enterprise: true },
        ],
    },
    {
        name: 'Security & Customisation',
        icon: <Shield size={15} />,
        rows: [
            { label: 'Role-based access',      free: false,      starter: true,            growth: true,           enterprise: true },
            { label: 'SSO / SAML',             free: false,      starter: false,           growth: false,          enterprise: true },
            { label: 'Audit logs',             free: false,      starter: false,           growth: true,           enterprise: true },
            { label: 'Custom domain',          free: false,      starter: false,           growth: false,          enterprise: true },
            { label: 'White-label branding',   free: false,      starter: false,           growth: false,          enterprise: true },
        ],
    },
    {
        name: 'AI & Automation',
        icon: <Zap size={15} />,
        rows: [
            { label: 'Smart ticket routing',   free: false,      starter: false,           growth: true,           enterprise: true },
            { label: 'AI suggested replies',   free: false,      starter: false,           growth: 'Coming soon',  enterprise: 'Coming soon' },
            { label: 'Automation rules',       free: false,      starter: '5 rules',       growth: 'Unlimited',    enterprise: 'Unlimited' },
            { label: 'AI chatbot (add-on)',    free: false,      starter: false,           growth: true,           enterprise: true },
        ],
    },
];

const ADDONS = [
    {
        icon: <Zap size={22} />,
        name: 'Extra Ticket Pack',
        description: 'Need more volume? Add 500 extra tickets to any paid plan instantly.',
        price: '₹299',
        unit: '/ 500 tickets',
        badge: null,
    },
    {
        icon: <Smartphone size={22} />,
        name: 'White-Label Mobile App',
        description: 'Deploy a branded mobile app (iOS & Android) with your logo, colours, and domain.',
        price: '₹4,999',
        unit: '/ month',
        badge: 'Now Live',
    },
    {
        icon: <MessageCircle size={22} />,
        name: 'AI Chatbot',
        description: 'Resolve common queries automatically before they become tickets. Powered by KaizIQ AI.',
        price: '₹1,999',
        unit: '/ month',
        badge: 'Coming Soon',
    },
];

const FAQS = [
    {
        q: 'Can I switch plans at any time?',
        a: 'Yes. You can upgrade or downgrade your plan at any time from the billing settings. Upgrades take effect immediately; downgrades apply at the next billing cycle.',
    },
    {
        q: 'What happens when I exceed my ticket limit?',
        a: 'On paid plans (Starter, Growth), extra tickets are charged automatically at the overage rate — ₹10/ticket on Starter and ₹7.50/ticket on Growth. On the Free plan, new submissions are paused when the 10-ticket monthly limit is reached. You will receive email notifications as you approach your limit.',
    },
    {
        q: 'Is there a free trial for paid plans?',
        a: 'Yes. All paid plans come with a 14-day free trial, no credit card required. After the trial, you can choose to subscribe or move to the Free plan.',
    },
    {
        q: 'Are there limits on the number of agents?',
        a: 'No. All paid plans include unlimited agent seats. You only pay for the plan itself — add as many agents as your team needs without any extra per-seat charges.',
    },
    {
        q: 'What payment methods do you accept?',
        a: 'We accept all major credit/debit cards, UPI, net banking, and bank transfers via Razorpay. Annual plans can also be paid via invoice.',
    },
    {
        q: 'Is my data secure?',
        a: 'Absolutely. All data is encrypted at rest and in transit using AES-256 and TLS 1.3. We are SOC 2 Type II compliant and follow GDPR data-handling standards.',
    },
    {
        q: 'Can I use KaizIQ Resolve for multiple brands or projects?',
        a: 'Yes. The Growth and Enterprise plans support multiple workspaces and forms. Enterprise customers can configure fully separate white-labeled environments per brand.',
    },
    {
        q: 'Do you offer discounts for NGOs or startups?',
        a: 'Yes. We offer special pricing for non-profits, early-stage startups, and educational institutions. Please reach out to our sales team to discuss eligibility.',
    },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

function BillingToggle({ annual, onToggle }) {
    return (
        <div className="pricingPage__toggle">
            <span className={!annual ? 'pricingPage__toggle__label--active' : 'pricingPage__toggle__label'}>
                Monthly
            </span>
            <button
                className={'pricingPage__toggle__btn' + (annual ? ' pricingPage__toggle__btn--on' : '')}
                onClick={onToggle}
                aria-label="Toggle billing period"
            >
                <motion.div
                    className="pricingPage__toggle__thumb"
                    animate={{ x: annual ? 20 : 2 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                />
            </button>
            <span className={annual ? 'pricingPage__toggle__label--active' : 'pricingPage__toggle__label'}>
                Annual
            </span>
            <span className="pricingPage__toggle__badge">Save up to 20%</span>
        </div>
    );
}

function PriceDisplay({ plan, annual }) {
    if (plan.monthlyPrice === null) {
        return (
            <div className="pricingPage__card__price">
                <span className="pricingPage__card__price__custom">Custom</span>
            </div>
        );
    }

    const price = annual ? plan.annualPrice : plan.monthlyPrice;

    return (
        <div className="pricingPage__card__price">
            {price === 0 ? (
                <span className="pricingPage__card__price__amount">Free</span>
            ) : (
                <>
                    <span className="pricingPage__card__price__symbol">₹</span>
                    <AnimatePresence mode="wait">
                        <motion.span
                            key={price}
                            className="pricingPage__card__price__amount"
                            initial={{ opacity: 0, y: -12 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 12 }}
                            transition={{ duration: 0.18 }}
                        >
                            {price.toLocaleString('en-IN')}
                        </motion.span>
                    </AnimatePresence>
                </>
            )}
        </div>
    );
}

function FeatureCell({ value }) {
    if (value === true) return <Check size={16} className="pricingPage__table__check" />;
    if (value === false) return <X size={14} className="pricingPage__table__cross" />;
    return <span className="pricingPage__table__text">{value}</span>;
}

function ComparisonTable() {
    const [openCats, setOpenCats] = useState(() => new Set(FEATURE_CATEGORIES.map((c) => c.name)));

    const toggle = (name) => {
        setOpenCats((prev) => {
            const next = new Set(prev);
            next.has(name) ? next.delete(name) : next.add(name);
            return next;
        });
    };

    return (
        <div className="pricingPage__table">
            {/* Header */}
            <div className="pricingPage__table__header">
                <div className="pricingPage__table__header__label">Features</div>
                {PLANS.map((p) => (
                    <div key={p.id} className={'pricingPage__table__header__plan' + (p.popular ? ' pricingPage__table__header__plan--popular' : '')}>
                        {p.name}
                        {p.popular && <span className="pricingPage__table__header__pop">★</span>}
                    </div>
                ))}
            </div>

            {/* Categories */}
            {FEATURE_CATEGORIES.map((cat) => {
                const open = openCats.has(cat.name);
                return (
                    <div key={cat.name} className="pricingPage__table__cat">
                        <button className="pricingPage__table__cat__head" onClick={() => toggle(cat.name)}>
                            <span className="pricingPage__table__cat__icon">{cat.icon}</span>
                            <span className="pricingPage__table__cat__name">{cat.name}</span>
                            <motion.span
                                className="pricingPage__table__cat__chevron"
                                animate={{ rotate: open ? 180 : 0 }}
                                transition={{ duration: 0.2 }}
                            >
                                <ChevronDown size={16} />
                            </motion.span>
                        </button>

                        <AnimatePresence initial={false}>
                            {open && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.22, ease: 'easeInOut' }}
                                    style={{ overflow: 'hidden' }}
                                >
                                    {cat.rows.map((row, i) => (
                                        <div key={i} className="pricingPage__table__row">
                                            <div className="pricingPage__table__row__label">{row.label}</div>
                                            <div className="pricingPage__table__row__cell"><FeatureCell value={row.free} /></div>
                                            <div className="pricingPage__table__row__cell"><FeatureCell value={row.starter} /></div>
                                            <div className="pricingPage__table__row__cell pricingPage__table__row__cell--popular"><FeatureCell value={row.growth} /></div>
                                            <div className="pricingPage__table__row__cell"><FeatureCell value={row.enterprise} /></div>
                                        </div>
                                    ))}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                );
            })}
        </div>
    );
}

function FaqItem({ faq, open, onToggle }) {
    return (
        <div className={'pricingPage__faq__item' + (open ? ' pricingPage__faq__item--open' : '')}>
            <button className="pricingPage__faq__q" onClick={onToggle}>
                <span>{faq.q}</span>
                <motion.span
                    className="pricingPage__faq__chevron"
                    animate={{ rotate: open ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                >
                    <ChevronDown size={18} />
                </motion.span>
            </button>
            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        className="pricingPage__faq__a"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.22, ease: 'easeInOut' }}
                        style={{ overflow: 'hidden' }}
                    >
                        <div className="pricingPage__faq__a__inner">{faq.a}</div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function PricingPage() {

    const navigate = useNavigate();
    const [annual, setAnnual] = useState(false);
    const [openFaq, setOpenFaq] = useState(0);
    const className = 'pricingPage';

    const seo = useSEO({
        title:       'Pricing — Simple, Transparent Plans',
        description: 'KaizIQ Resolve pricing: Free, Starter (₹5,990/mo), Growth (₹9,990/mo), and Enterprise. Unlimited agents on all paid plans. No hidden fees. Start free, no credit card required.',
        canonical:   'https://resolve.kaiziq.io/#/pricing',
        schema: [
            {
                '@context': 'https://schema.org',
                '@type':    'WebPage',
                name:       'KaizIQ Resolve Pricing',
                url:        'https://resolve.kaiziq.io/#/pricing',
                description: 'Transparent pricing plans for ticket management — Free, Starter, Growth, Enterprise.',
                breadcrumb: { '@type': 'BreadcrumbList', itemListElement: [{ '@type': 'ListItem', position: 1, name: 'Home', item: 'https://resolve.kaiziq.io/' }, { '@type': 'ListItem', position: 2, name: 'Pricing', item: 'https://resolve.kaiziq.io/#/pricing' }] },
            },
            {
                '@context': 'https://schema.org',
                '@type':    'FAQPage',
                mainEntity: FAQS.map(f => ({
                    '@type':          'Question',
                    name:             f.q,
                    acceptedAnswer:   { '@type': 'Answer', text: f.a },
                })),
            },
        ],
    });

    const handlePlanCta = (plan) => {
        if (plan.id === 'enterprise') {
            // TODO: open contact sales modal / navigate to contact page
            return;
        }
        navigate('/login');
    };

    return (
        <div className={className}>
            {seo}
            <TopPanel />

            {/* ── Hero ──────────────────────────────────────────────────── */}
            <section className={className + '__hero'}>
                <FloatingPaths position={1} />
                <FloatingPaths position={-1} />
                <div className={className + '__hero__inner'}>
                    <motion.div
                        className={className + '__hero__tag'}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        Simple &amp; transparent pricing
                    </motion.div>
                    <motion.h1
                        className={className + '__hero__title'}
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                    >
                        KaizIQ Resolve Pricing
                    </motion.h1>
                    <motion.p
                        className={className + '__hero__sub'}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                    >
                        The right plan for every team size. Start free, scale when you're ready.
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                    >
                        <BillingToggle annual={annual} onToggle={() => setAnnual((v) => !v)} />
                    </motion.div>
                </div>
            </section>

            {/* ── Plan cards ────────────────────────────────────────────── */}
            <section className={className + '__cards'}>
                <div className={className + '__cards__grid'}>
                    {PLANS.map((plan, i) => (
                        <motion.div
                            key={plan.id}
                            className={
                                className + '__card' +
                                (plan.popular ? ' ' + className + '__card--popular' : '')
                            }
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.45, delay: i * 0.08 }}
                        >
                            {plan.popular && (
                                <div className={className + '__card__badge'}>
                                    <Star size={11} fill="currentColor" /> Most Popular
                                </div>
                            )}

                            <div className={className + '__card__top'}>
                                <h3 className={className + '__card__name'}>{plan.name}</h3>
                                <p className={className + '__card__tagline'}>{plan.tagline}</p>
                            </div>

                            <PriceDisplay plan={plan} annual={annual} />
                            <p className={className + '__card__price__note'}>{plan.priceNote}</p>

                            <ul className={className + '__card__highlights'}>
                                <li><Check size={14} /><span>{plan.agents}</span></li>
                                <li><Check size={14} /><span>{plan.forms}</span></li>
                                <li><Check size={14} /><span>{plan.tickets}</span></li>
                                {plan.overage && <li><Check size={14} /><span>{plan.overage}</span></li>}
                            </ul>

                            <button
                                className={className + '__card__cta ' + className + '__card__cta--' + plan.ctaVariant}
                                onClick={() => handlePlanCta(plan)}
                            >
                                {plan.cta}
                            </button>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* ── Feature comparison ────────────────────────────────────── */}
            <section className={className + '__compare'}>
                <div className={className + '__section-head'}>
                    <h2 className={className + '__section-title'}>Compare all features</h2>
                    <p className={className + '__section-sub'}>Click a category to expand or collapse its feature rows.</p>
                </div>
                <ComparisonTable />
            </section>

            {/* ── Add-ons ───────────────────────────────────────────────── */}
            <section className={className + '__addons'}>
                <div className={className + '__section-head'}>
                    <h2 className={className + '__section-title'}>Power-ups &amp; Add-ons</h2>
                    <p className={className + '__section-sub'}>Extend any paid plan with these optional extras.</p>
                </div>
                <div className={className + '__addons__grid'}>
                    {ADDONS.map((addon, i) => (
                        <motion.div
                            key={addon.name}
                            className={className + '__addon'}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: i * 0.1 }}
                        >
                            {addon.badge && (
                                <span className={
                                    className + '__addon__badge' +
                                    (addon.badge === 'Now Live' ? ' ' + className + '__addon__badge--live' : ' ' + className + '__addon__badge--soon')
                                }>
                                    {addon.badge}
                                </span>
                            )}
                            <div className={className + '__addon__icon'}>{addon.icon}</div>
                            <h4 className={className + '__addon__name'}>{addon.name}</h4>
                            <p className={className + '__addon__desc'}>{addon.description}</p>
                            <div className={className + '__addon__price'}>
                                <span className={className + '__addon__price__amount'}>{addon.price}</span>
                                <span className={className + '__addon__price__unit'}>{addon.unit}</span>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* ── FAQ ───────────────────────────────────────────────────── */}
            <section className={className + '__faq'}>
                <div className={className + '__section-head'}>
                    <h2 className={className + '__section-title'}>Frequently asked questions</h2>
                </div>
                <div className={className + '__faq__list'}>
                    {FAQS.map((faq, i) => (
                        <FaqItem
                            key={i}
                            faq={faq}
                            open={openFaq === i}
                            onToggle={() => setOpenFaq(openFaq === i ? null : i)}
                        />
                    ))}
                </div>
            </section>

            {/* ── CTA Banner ────────────────────────────────────────────── */}
            <section className={className + '__cta'}>
                <motion.div
                    className={className + '__cta__inner'}
                    initial={{ opacity: 0, scale: 0.97 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                >
                    <h2 className={className + '__cta__title'}>Ready to streamline your support?</h2>
                    <p className={className + '__cta__sub'}>
                        Join hundreds of teams using KaizIQ Resolve. Start for free — no credit card required.
                    </p>
                    <div className={className + '__cta__actions'}>
                        <button className={className + '__cta__btn--primary'} onClick={() => navigate('/login')}>
                            Get Started Free
                        </button>
                        <button className={className + '__cta__btn--ghost'} onClick={() => navigate('/login')}>
                            Book a Demo
                        </button>
                    </div>
                </motion.div>
            </section>

            <Footer />
        </div>
    );
}
